import { config } from "@/app/config";
import { AuthUrls } from "@/modules/auth/common/enum";
import { AppPaths, LanguageKey, LanguageValue, Storage, } from "@/shared/common/enums";
import {
  clearUserFromStorage,
  getFromLocalStorage,
  getFromSessionStorage,
} from "@/shared/helpers";
import axios, { type AxiosError, type InternalAxiosRequestConfig, } from "axios";

type JsonPrimitive = string | number | boolean | null;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

const isEmptyObject = (v: JsonValue) =>
  v !== null && typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0;

const cleanPayload = (value: JsonValue): JsonValue => {
  if (Array.isArray(value)) return value.map(cleanPayload);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, JsonValue>)
        .filter(([, v]) => v !== null && v !== "")
        .map(([k, v]) => [k, cleanPayload(v)] as const)
        .filter(([, v]) => !isEmptyObject(v))
    );
  }
  return value;
};

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (error: unknown) => void;
  config: InternalAxiosRequestConfig;
}[] = [];

const processQueue = (error: unknown, config?: InternalAxiosRequestConfig) => {
  failedQueue.forEach(({ resolve, reject, config: queuedConfig }) => {
    if (error) {
      reject(error);
    } else if (config) {
      resolve(api(queuedConfig));
    }
  });
  failedQueue = [];
};

export const api = axios.create({
  baseURL: config.appBaseUrl,
  withCredentials: true,
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (config.headers) {
    const lang = (getFromLocalStorage(Storage.Lang) ||
      LanguageKey.Hy) as keyof typeof LanguageValue;
    const browserName = getFromSessionStorage(Storage.BrowserName);
    const browserVersion = getFromSessionStorage(Storage.BrowserVersion);
    const browserOs = getFromSessionStorage(Storage.BrowserOs);

    config.headers["Accept-Language"] = LanguageValue[lang];
    config.headers["Model"] = browserName;
    config.headers["DeviceOS"] = browserOs;
    config.headers["DeviceId"] = "MyAmeriaBusiness Web";
    config.headers["DeviceOSVersion"] = browserVersion;
    config.headers["Platform"] = "Web";
  }
  if (config.data && !(config.data instanceof FormData)) {
    config.data = cleanPayload(config.data);
  }
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (originalRequest?.url?.includes(AuthUrls.RefreshToken)) {
      clearUserFromStorage();
      window.location.href = AppPaths.Login;
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post(AuthUrls.RefreshToken, {});
        processQueue(null, originalRequest);

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearUserFromStorage();
        window.location.href = AppPaths.Login;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);
